import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  Grid,
  Button,
} from "@mui/material";
import VariantPicker from "./VariantPicker";
import Qty from "./Qty";
import DateRow from "./DateRow";
import SummaryRow from "./SummaryRow";
import { lineDeposit, toISO } from "./utils";

export default function RentalCard(props) {
  const {
    product: productProp,
    selection: selectionProp,
    onUpsert,
  } = props || {};
  const p = productProp || {};
  const selection = selectionProp || null;

  const variants = Array.isArray(p.variants)
    ? p.variants.filter((v) => v?.active !== false)
    : [];
  const firstVariant = variants[0] || { id: "default", label: "Default" };

  const variantId = selection?.variantId || firstVariant.id;
  const variant = variants.find((v) => v.id === variantId) || firstVariant;

  const rawSrc = variant?.image || p?.images?.[0] || "";
  const mediaSrc = rawSrc ? encodeURI(rawSrc) : "";

  const start = selection?.startDate || "";
  const end = selection?.endDate || "";
  const days = selection?.days || 0;
  const qty = selection?.quantity || 0;

  const rate = Number(p?.dailyRate) || 0;
  const deposit = days && qty ? lineDeposit(p, variant, qty) : 0;
  const lineTotal = useMemo(() => rate * days * qty, [rate, days, qty]);

  const handleUpsert = (patch) => {
    const nextVariantId = patch.variantId ?? variantId;
    const chosen =
      variants.find((v) => v.id === nextVariantId) || variant || firstVariant;

    // ensure valid dates/qty when user starts interacting
    const today = toISO(new Date());
    const nextStart = (patch.startDate ?? start) || today;
    const nextEnd = (patch.endDate ?? end) || nextStart;
    const nextDays = (patch.days ?? days) || 1;
    const nextQty = Math.max(0, patch.quantity ?? qty ?? 0);

    onUpsert?.({
      productId: p.id,
      variantId: nextVariantId,
      startDate: nextStart,
      endDate: nextEnd,
      days: nextDays,
      quantity: nextQty,
      productTitle: p.title,
      variantLabel: variants.length > 1 ? chosen?.label : undefined,
      unitLabel: p.unitLabel || "set",
      attributes: chosen?.attributes || {},
    });
  };

  const handleRemove = () => {
    handleUpsert({
      quantity: 0,
      days: 0,
      startDate: "",
      endDate: "",
    });
  };

  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 3, overflow: "hidden", height: "100%" }}>
      {/* Media */}
      {mediaSrc ? (
        <Box
          sx={{
            width: "100%",
            aspectRatio: "4 / 3",
            bgcolor: (t) => t.palette.action.hover,
            display: "grid",
            placeItems: "center",
            overflow: "hidden",
          }}>
          <Box
            component="img"
            src={mediaSrc}
            alt={p.title || "Rental"}
            onError={(e) => {
              if (!e.currentTarget.dataset.fbk) {
                e.currentTarget.dataset.fbk = "1";
                e.currentTarget.src = "/placeholder.png";
              }
            }}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            aspectRatio: "4 / 3",
            bgcolor: (t) => t.palette.action.hover,
          }}
        />
      )}

      <CardContent sx={{ p: 2.25 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {p.title || "Rental"}
        </Typography>

        {/* Rate chip */}
        <Box
          sx={{
            mt: 1,
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "center",
          }}>
          <Chip size="small" label={`$${rate.toFixed(2)}/day`} />
          {p.unitLabel === "set" && (
            <Chip size="small" variant="outlined" label="Full set" />
          )}
        </Box>

        {/* Variants only if more than 1 (e.g., color) */}
        <VariantPicker
          variants={variants}
          value={variantId}
          onChange={(v) =>
            handleUpsert({ variantId: v, quantity: Math.max(1, qty || 1) })
          }
        />

        {/* Controls */}
        <Box sx={{ mt: 1.25 }}>
          <Grid container spacing={1.25} alignItems="center">
            <Grid item xs>
              <DateRow
                start={start}
                end={end}
                ensureQty={(min) => Math.max(min, qty || 0)}
                onChange={(patch) => handleUpsert(patch)}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 1.25 }}>
          <Grid container spacing={1.25} alignItems="center">
            <Grid item xs="auto">
              <Qty
                value={qty}
                onInc={(q) => handleUpsert({ quantity: q })}
                onDec={(q) => handleUpsert({ quantity: q })}
              />
            </Grid>
            {qty > 0 && start && end && (
              <Grid item xs="auto">
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleRemove}
                  sx={{ textTransform: "none" }}>
                  Remove
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>

        <SummaryRow
          show={qty > 0 && !!start && !!end}
          quantity={qty}
          unitLabel={p.unitLabel || "set"}
          variantLabel={variants.length > 1 ? variant?.label : undefined}
          startDate={start}
          endDate={end}
          days={days}
          lineTotal={lineTotal}
          deposit={deposit}
        />
      </CardContent>
    </Card>
  );
}
